<div class="container">
  <h1>Plan &amp; Licensing</h1>
  <p class="subtitle">
    Included optimized-token allowance, then pick Developer Pro, Team Pro, or Enterprise.
    Manage Desktop / Companion license keys below.
  </p>

  <section class="saas-plans" aria-label="SDK and telemetry plans">
    <h2 class="saas-plans__title">AI cost optimization + observability</h2>
    <p class="saas-plans__lead">
      Free Tier for core workflows; Developer Pro, Team Pro, and Enterprise add included optimized tokens, savings sync, and team features. Enterprise is sales-led.
    </p>
    <div class="saas-plans__grid">
      <div class="saas-card" *ngFor="let p of saasPlans">
        <h3>{{ p.name }}</h3>
        <p class="saas-card__price">{{ p.price }}</p>
        <ul>
          <li *ngFor="let b of p.bullets">{{ b }}</li>
        </ul>
      </div>
    </div>
  </section>

  <div *ngIf="loading" class="loading">
    <p>Loading...</p>
  </div>

  <div *ngIf="!loading && entitlement" class="billing-content">

    <div class="card" [ngClass]="{
      'active': entitlement.licenseStatus === 'valid',
      'expired': entitlement.licenseStatus === 'expired'
    }">
      <h2>{{ workspacePlanHeadline }}</h2>

      <div *ngIf="entitlement.licenseStatus === 'valid' && billingExemptWorkspace" class="success-message">
        This workspace is exempt from billing: all Spectyra features stay available without checkout.
      </div>

      <div *ngIf="entitlement.licenseStatus === 'valid' && paidSubscriptionActive && !billingExemptWorkspace" class="success-message">
        Paid subscription is active — you have full cloud features for the tier you subscribed to.
      </div>

      <div *ngIf="entitlement.licenseStatus === 'valid' && !paidSubscriptionActive && !billingExemptWorkspace && entitlement.plan === 'enterprise'" class="success-message">
        Enterprise is arranged with Spectyra sales (custom limits, security review, invoicing). Self-serve checkout below does not apply to this tier.
      </div>

      <div *ngIf="entitlement.licenseStatus === 'valid' && !paidSubscriptionActive && !billingExemptWorkspace && entitlement.plan !== 'enterprise' && entitlement.plan === 'free'" class="success-message">
        You are on the Free Tier. Observe mode and pass-through stay free; use checkout below when you need Developer Pro or Team Pro.
      </div>

      <div *ngIf="entitlement.licenseStatus === 'valid' && !paidSubscriptionActive && !billingExemptWorkspace && entitlement.plan !== 'enterprise' && entitlement.plan !== 'free'" class="success-message">
        This workspace shows <strong>{{ assignedPlanMarketing }}</strong> without an active paid subscription. If that is unexpected, open
        <a routerLink="/usage">Usage</a> or contact support.
      </div>

      <div *ngIf="entitlement.licenseStatus === 'expired' || entitlement.licenseStatus === 'missing'" class="error-message">
        Optimized runs and full cloud savings require an active paid tier. See Developer Pro, Team Pro, and Enterprise above, then continue to checkout.
      </div>

      <!-- Optimized Run Usage -->
      <div class="run-usage" *ngIf="entitlement.optimizedRunsLimit !== null">
        <h3>Optimized Runs (mode = on)</h3>
        <div class="usage-bar-wrapper">
          <div class="usage-bar">
            <div class="usage-bar-fill"
                 [style.width.%]="runUsagePercent"
                 [ngClass]="{ 'warn': runUsagePercent > 80, 'critical': runUsagePercent >= 100 }">
            </div>
          </div>
          <span class="usage-text">
            {{ entitlement.optimizedRunsUsed }} / {{ entitlement.optimizedRunsLimit }}
          </span>
        </div>
        <p class="usage-note">
          Observe mode and pass-through are always free.
          Only "on" runs count toward your limit.
        </p>
      </div>

      <div *ngIf="entitlement.optimizedRunsLimit === null" class="success-message">
        Unlimited optimized runs
      </div>

      <div class="checkout-plan-pick" *ngIf="showPaidCheckoutCta && showStripeSelfServeCheckout">
        <h3 class="checkout-plan-pick__title">Choose a paid plan</h3>
        <p class="checkout-plan-pick__hint">
          Each option uses its own Stripe recurring price (set <code>STRIPE_PRICE_DEVELOPER_PRO</code> /
          <code>STRIPE_PRICE_TEAM_PRO</code> on the API, or legacy <code>STRIPE_PRICE_ID</code> for Developer Pro only).
        </p>
        <div class="checkout-plan-grid" role="radiogroup" aria-label="Self-serve plan">
          <label
            *ngFor="let p of selfServePlanCards"
            class="checkout-plan-option"
            [class.checkout-plan-option--disabled]="!isCheckoutPlanSelectable(p.id)"
            [class.checkout-plan-option--active]="selectedCheckoutPlan === p.id"
          >
            <input
              type="radio"
              name="saas_checkout_plan"
              [value]="p.id"
              [(ngModel)]="selectedCheckoutPlan"
              [disabled]="!isCheckoutPlanSelectable(p.id)"
            />
            <span class="checkout-plan-option__body">
              <span class="checkout-plan-option__name">{{ p.name }}</span>
              <span class="checkout-plan-option__price">{{ p.price }}</span>
            </span>
          </label>
        </div>
      </div>

      <button class="btn-primary"
              *ngIf="showPaidCheckoutCta && showStripeSelfServeCheckout"
              (click)="upgrade()"
              [disabled]="upgrading">
        {{ upgrading ? 'Processing...' : 'Continue to secure checkout' }}
      </button>
    </div>

    <!-- Feature Access -->
    <div class="card info">
      <h3>Feature Access</h3>
      <div class="feature-grid">
        <div class="feature-item">
          <span class="feature-icon" [class.enabled]="entitlement.sdkEnabled">●</span>
          SDK Integration
        </div>
        <div class="feature-item">
          <span class="feature-icon" [class.enabled]="entitlement.desktopAppEnabled">●</span>
          Desktop App
        </div>
        <div class="feature-item">
          <span class="feature-icon" [class.enabled]="entitlement.cloudAnalyticsEnabled">●</span>
          Cloud Analytics Sync
        </div>
        <div class="feature-item">
          <span class="feature-icon enabled">●</span>
          Observe Mode (always free)
        </div>
      </div>
    </div>

    <!-- License Keys -->
    <div class="card">
      <h2>Desktop / Companion License Keys</h2>
      <p class="section-desc">
        License keys let the Desktop App and Local Companion validate your
        entitlement offline. Generate one per device.
      </p>

      <!-- New key banner -->
      <div class="new-key-banner" *ngIf="newLicenseKey">
        <p><strong>Save this key — it will not be shown again.</strong></p>
        <code class="license-key-display">{{ newLicenseKey }}</code>
        <button class="btn-secondary" (click)="copyKey()">Copy</button>
      </div>

      <!-- Generate form -->
      <div class="generate-form">
        <input type="text"
               [(ngModel)]="newDeviceName"
               placeholder="Device name (optional)"
               class="input" />
        <button class="btn-primary"
                (click)="generateKey()"
                [disabled]="generatingKey">
          {{ generatingKey ? 'Generating...' : 'Generate License Key' }}
        </button>
      </div>

      <!-- Active keys -->
      <div *ngIf="activeKeys.length > 0" class="key-list">
        <h3>Active Keys</h3>
        <div class="key-row" *ngFor="let key of activeKeys">
          <div class="key-info">
            <span class="key-prefix">{{ key.key_prefix }}…</span>
            <span class="key-device" *ngIf="key.device_name">{{ key.device_name }}</span>
            <span class="key-meta">Created {{ formatDate(key.created_at) }}</span>
            <span class="key-meta" *ngIf="key.last_validated_at">
              Last validated {{ formatDate(key.last_validated_at) }}
            </span>
          </div>
          <button class="btn-danger-sm" (click)="revokeKey(key.id)">Revoke</button>
        </div>
      </div>

      <p *ngIf="activeKeys.length === 0" class="empty-state">
        No active license keys. Generate one to use with the Desktop App.
      </p>
    </div>

    <!-- Self-service: cancel / pause / delete -->
    <div class="card account-actions" *ngIf="!loading">
      <h2>Subscription &amp; account</h2>
      <p class="section-desc">
        Manage your paid plan, pause your cloud account, or permanently delete your account.
        Pausing stops future charges and gives you a grace period before read-only mode; cancelling a subscription stops renewal at the end of the current period.
      </p>

      <div class="account-row" *ngIf="accountSummary?.has_cancellable_subscription && !anySubScheduledCancel()">
        <div>
          <strong>Cancel paid renewal</strong>
          <p class="hint">Stops auto-renew at the end of the current billing period. You keep access until then.</p>
        </div>
        <button type="button" class="btn-secondary" (click)="cancelPaidAtPeriodEnd()" [disabled]="accountActionBusy">
          Cancel renewal
        </button>
      </div>

      <div class="account-row" *ngIf="anySubScheduledCancel()">
        <div>
          <strong>Renewal cancelled</strong>
          <p class="hint">Your subscription will end after the current period. You can resume auto-renew below.</p>
        </div>
        <button type="button" class="btn-primary" (click)="keepSubscriptionRenewal()" [disabled]="accountActionBusy">
          Keep subscription
        </button>
      </div>

      <div
        class="account-row"
        *ngIf="accountSummary && (accountSummary.access_state === 'active' || !accountSummary.access_state)"
      >
        <div>
          <strong>Pause account</strong>
          <p class="hint">Stops charging; full savings access for 30 days, then read-only API until you reactivate.</p>
        </div>
        <button type="button" class="btn-secondary" (click)="pauseMyAccount()" [disabled]="accountActionBusy">
          Pause account
        </button>
      </div>

      <p class="hint subtle" *ngIf="accountSummary?.access_state === 'inactive'">
        Your account is set to <strong>Inactive</strong> by an administrator. Contact support to restore full savings, or manage billing below.
      </p>

      <div class="account-row" *ngIf="accountSummary?.access_state === 'paused'">
        <div>
          <strong>Paused</strong>
          <p class="hint" *ngIf="accountSummary?.pause_savings_until">
            Savings grace until {{ formatDate(accountSummary!.pause_savings_until!) }} — then read-only until reactivated.
          </p>
          <p class="hint" *ngIf="!accountSummary?.pause_savings_until">Reactivate to restore full cloud access and billing.</p>
        </div>
        <button type="button" class="btn-primary" (click)="resumeMyAccount()" [disabled]="accountActionBusy">
          Reactivate account
        </button>
      </div>

      <div class="account-row danger-zone">
        <div>
          <strong>Delete account</strong>
          <p class="hint">Removes org data you own, memberships, and your login. This cannot be undone.</p>
          <input
            type="text"
            class="input confirm-input"
            [(ngModel)]="deleteConfirmText"
            placeholder="Type DELETE_MY_ACCOUNT to enable"
            autocomplete="off"
          />
        </div>
        <button
          type="button"
          class="btn-danger"
          (click)="deleteMyAccount()"
          [disabled]="accountActionBusy || deleteConfirmText !== 'DELETE_MY_ACCOUNT'"
        >
          Delete my account
        </button>
      </div>
    </div>

    <!-- Organization Info -->
    <div class="card info" *ngIf="billingStatus?.org">
      <h3>Organization</h3>
      <p><strong>Name:</strong> {{ billingStatus.org.name }}</p>
      <p><strong>ID:</strong> {{ billingStatus.org.id }}</p>
    </div>
  </div>

  <div *ngIf="error" class="error">
    <p>{{ error }}</p>
  </div>
</div>
